/**
 * Deep Seat Couch Cushion Configurator — Pricing Engine + Interactive SVG Preview
 * uscushion.com · 2026-07
 *
 * Deep seat = seat cushion only OR seat + back cushion set (two separate pieces)
 * Pricing: bench formula applied to each piece
 * SVG: isometric seat + upright back model (same as RV perspective)
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench) ─── */
  var FABRIC_PRICE_RMB  = 25;
  /* Premium fabric grades (RMB per m2); standard falls back to FABRIC_PRICE_RMB. Driven by fabric `grade:` tag. */
  var FABRIC_RATE_RMB   = { marine: 54 };
  function fabricRate() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return FABRIC_RATE_RMB[g] || FABRIC_PRICE_RMB;
  }
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MARINE_COST_RATIO = 0.38;   /* Marine fabric target margin 62% (standard 60%); used when grade:marine is selected */
  var COVER_COST_RATIO  = 0.55;   /* Extra covers = 45% margin (AOV add-on, lower than cushion's 60/62%); same for standard & marine */
  function targetRatio() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return g === 'marine' ? MARINE_COST_RATIO : TARGET_COST_RATIO;
  }
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };

  var LABELS = {
    none: 'None', single: 'Single piping', double: 'Double piping',
    match: 'Match fabric', white: 'White piping', black: 'Black piping', navy: 'Navy piping', cream: 'Cream piping',
    two: '2 straps', four: '4 straps',
    regular: 'Regular zipper', invisible: 'Invisible zipper',
    velcro: 'Velcro', black_pad: 'Black pad', beige_pad: 'Beige pad'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    backStyle: '',
    seatL: 0, seatW: 0, seatT: 0,
    backL: 0, backW: 0, backT: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Pricing (bench formula, reused for each piece) ─── */
  function calcPiecePrice(L, W, T) {
    if (!L || !W || !T) return null;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var foamVolume = Lm * Wm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + foamVolume * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / targetRatio() * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  /* Fabric-only cover price for one rectangular piece (no foam), at COVER_COST_RATIO. */
  function coverPiece(L, W, T) {
    if (!L || !W || !T) return 0;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var weight = fabricArea * FABRIC_GSM / 1000; // fabric weight only — no foam
    var totalRMB = fabricArea * fabricRate() + weight * SHIPPING_RMB + ACCESSORIES_RMB;
    return totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / COVER_COST_RATIO;
  }

  /* One extra cover SET = seat cover + back cover (two rectangles summed),
     fabric grade auto via fabricRate. Replaces the flat $30/$50 fee. */
  function extraCoverUnit() {
    return Math.round(coverPiece(state.seatL, state.seatW, state.seatT)
      + coverPiece(state.backL, state.backW, state.backT));
  }

  function updateExtraCoverLabels() {
    if (!els.extraCovers) return;
    var unit = extraCoverUnit();
    var opts = els.extraCovers.options;
    for (var i = 0; i < opts.length; i++) {
      var n = parseInt(opts[i].value) || 0;
      if (n === 0) { opts[i].text = 'No extra cover'; continue; }
      var label = (n === 1 ? '1 set' : n + ' sets');
      opts[i].text = unit > 0 ? label + ' (+$' + (unit * n) + ')' : label;
    }
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + extraCoverUnit() * (parseInt(state.extraCovers) || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0) + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasSeatDims() {
    return state.seatL >= 6 && state.seatW >= 6 && state.seatT >= 1;
  }
  function hasBackDims() {
    return state.backL >= 6 && state.backW >= 6 && state.backT >= 1;
  }
  function isComplete() {
    return state.backStyle && hasSeatDims() && hasBackDims()
      && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — Isometric: seat + upright back
     ═══════════════════════════════════════════════════════════ */

  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    var sL = state.seatL, sW = state.seatW, sT = state.seatT;
    var bL = state.backL, bW = state.backW, bT = state.backT;
    var empty = (!sL && !sW && !sT && !bL && !bW && !bT);

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    /* ── Resolve face colors from fabric ── */
    var topC = '#EDE9E0', frontC = '#D5D0C2', sideC = '#E4E0D4', strokeC = '#2A2A28';
    var fabricLum = 200;
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50);
        sideC = lighten(baseHex, 0.35);
        frontC = lighten(baseHex, 0.20);
        strokeC = darken(baseHex, 0.45);
        var sc = hexToRgb(strokeC);
        fabricLum = (sc[0] + sc[1] + sc[2]) / 3;
        if (fabricLum > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex);
        fabricLum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }

    var h = '';

    /* ── Defs ── */
    h += '<defs><marker id="ds-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    /* ── Isometric direction vectors ── */
    var dirLen   = [0.98776, -0.15596];
    var dirWidth = [-0.35120, -0.93660];

    /* ── Seat geometry ── */
    var seatLenPx, seatWidthDiagPx, seatH;
    if (empty) {
      seatLenPx = 200; seatWidthDiagPx = 80; seatH = 21;
    } else {
      var usL = sL || 48, usW = sW || 22, usT = sT || 4;
      var sRatio = clamp(usL / usW, 1, 5);
      var sFwPx = clamp(280 / (1 + sRatio * 0.4), 60, 160);
      seatLenPx = clamp(sFwPx * sRatio, 90, 220);
      seatWidthDiagPx = clamp(sFwPx * 0.45, 30, 90);
      var sTf = clamp(usT / usW * 1.2, 0.08, 0.35);
      seatH = clamp(sFwPx * sTf, 10, 42);
    }

    var origin = [165, 241];
    var sA = origin;
    var sB = [sA[0] + dirWidth[0] * seatWidthDiagPx, sA[1] + dirWidth[1] * seatWidthDiagPx];
    var sD = [sA[0] + dirLen[0] * seatLenPx,         sA[1] + dirLen[1] * seatLenPx];
    var sC = [sB[0] + dirLen[0] * seatLenPx,          sB[1] + dirLen[1] * seatLenPx];

    var sFbl = [sA[0], sA[1] + seatH];
    var sFbr = [sD[0], sD[1] + seatH];
    var sRbr = [sC[0], sC[1] + seatH];
    var sRbl = [sB[0], sB[1] + seatH];

    /* ── Back geometry ── */
    var backHeightPx, backThickPx;
    if (empty) {
      backHeightPx = 90; backThickPx = 17;
    } else {
      var ubW = bW || 16, ubT = bT || 3;
      backHeightPx = clamp(ubW * 3.5, 40, 130);
      backThickPx = clamp(ubT * 5, 8, 35);
    }

    var bFL = [sB[0], sB[1]];
    var bFR = [sC[0], sC[1]];
    var bFLt = [bFL[0], bFL[1] - backHeightPx];
    var bFRt = [bFR[0], bFR[1] - backHeightPx];
    var bBL = [bFL[0] + dirWidth[0] * backThickPx, bFL[1] + dirWidth[1] * backThickPx];
    var bBR = [bFR[0] + dirWidth[0] * backThickPx, bFR[1] + dirWidth[1] * backThickPx];
    var bBLt = [bBL[0], bBL[1] - backHeightPx];
    var bBRt = [bBR[0], bBR[1] - backHeightPx];

    var backTopC = lighten(topC, 0.05);
    var backFrontC = darken(frontC, 0.05);
    var backSideC = darken(sideC, 0.05);

    /* ── Draw back cushion (behind seat) ── */
    h += '<polygon points="' + [bFL, bFR, bFRt, bFLt].map(pt).join(' ') + '" fill="' + backFrontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    h += '<polygon points="' + [bFLt, bFRt, bBRt, bBLt].map(pt).join(' ') + '" fill="' + backTopC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    h += '<polygon points="' + [bFL, bFLt, bBLt, bBL].map(pt).join(' ') + '" fill="' + backSideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    if (state.piping === 'single' || state.piping === 'double') {
      var pipCb = '#F5F1E8', pwb = 2.2;
      h += '<polyline points="' + [bFL, bFR, bFRt, bFLt, bFL].map(pt).join(' ')
        + '" fill="none" stroke="' + pipCb + '" stroke-width="' + pwb + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      if (state.piping === 'double') {
        h += '<polyline points="' + [bBL, bBLt, bBRt].map(pt).join(' ')
          + '" fill="none" stroke="' + pipCb + '" stroke-width="' + pwb + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      }
    }

    /* ── Straps (behind seat face) ── */
    if ((state.straps === 'two' || state.straps === 'four') && !empty) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47';
      var hangLen = 16;
      function drawTies(cx_, cy_, dirX) {
        var spread = 4;
        h += '<path d="M' + f(cx_ - spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ - spread + dirX * 3) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ - spread * 1.5 + dirX * 5) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(cx_ + spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ + spread + dirX * 2) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ + spread * 1.5 + dirX * 4) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }
      drawTies(sFbl[0], sFbl[1], -1);
      drawTies(sFbr[0], sFbr[1], 1);
      if (state.straps === 'four') {
        drawTies(sRbl[0], sRbl[1], -1);
        drawTies(sRbr[0], sRbr[1], 1);
      }
    }

    /* ── Draw seat cushion ── */
    h += '<polygon points="' + [sB, sC, sD, sA].map(pt).join(' ') + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    h += '<polygon points="' + [sA, sD, sFbr, sFbl].map(pt).join(' ') + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    h += '<polygon points="' + [sA, sB, sRbl, sFbl].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    /* ── Piping on seat ── */
    if (state.piping === 'single' || state.piping === 'double') {
      var pipC2 = '#F5F1E8', pw2 = 2.2;
      h += '<polyline points="' + [sA, sB, sC, sD, sA].map(pt).join(' ')
        + '" fill="none" stroke="' + pipC2 + '" stroke-width="' + pw2 + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      if (state.piping === 'double') {
        h += '<polyline points="' + [sRbl, sFbl, sFbr].map(pt).join(' ')
          + '" fill="none" stroke="' + pipC2 + '" stroke-width="' + pw2 + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      }
    }

    /* ── Anti-slip on seat bottom ── */
    if (state.antislip && state.antislip !== 'none' && !empty) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }

      var padOffset = 1, padThick = 4, padInset = 5;
      var normalDir = [-dirLen[1], dirLen[0]];

      function stripQuad(p1, p2, t0, t1) {
        var edgeLen = Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
        var q1 = [p1[0] + (p2[0] - p1[0]) * (t0 / edgeLen), p1[1] + (p2[1] - p1[1]) * (t0 / edgeLen)];
        var q2 = [p1[0] + (p2[0] - p1[0]) * (t1 / edgeLen), p1[1] + (p2[1] - p1[1]) * (t1 / edgeLen)];
        var q1o = [q1[0] + normalDir[0] * padOffset, q1[1] + normalDir[1] * padOffset];
        var q2o = [q2[0] + normalDir[0] * padOffset, q2[1] + normalDir[1] * padOffset];
        var q3 = [q2[0] + normalDir[0] * (padOffset + padThick), q2[1] + normalDir[1] * (padOffset + padThick)];
        var q4 = [q1[0] + normalDir[0] * (padOffset + padThick), q1[1] + normalDir[1] * (padOffset + padThick)];
        return [q1o, q2o, q3, q4];
      }

      var edgeLenPx = Math.sqrt(Math.pow(sFbr[0] - sFbl[0], 2) + Math.pow(sFbr[1] - sFbl[1], 2));

      if (state.antislip === 'velcro') {
        var strips = 5, gapPx = edgeLenPx / strips;
        for (var vi = 0; vi < strips; vi++) {
          var t0 = vi * gapPx + 3, t1 = (vi + 1) * gapPx - 3;
          if (t1 <= t0) continue;
          var quad = stripQuad(sFbl, sFbr, t0, t1);
          h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
        }
      } else {
        var quad = stripQuad(sFbl, sFbr, padInset, edgeLenPx - padInset);
        h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
      }
    }

    /* ── Dimension labels ── */
    var dimC = '#1a4f78', thkC = '#3d5e3f', backDimC = '#6b3a1a';
    var ls = 10;
    var hug = 18;

    var centroid = [(sA[0] + sB[0] + sC[0] + sD[0]) / 4, (sA[1] + sB[1] + sC[1] + sD[1]) / 4];
    function outward(p1, p2) {
      var mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
      var vx = mid[0] - centroid[0], vy = mid[1] - centroid[1];
      var len = Math.sqrt(vx * vx + vy * vy) || 1;
      return [vx / len, vy / len];
    }

    var lblSL = sL ? ' — ' + sL + '″' : '';
    var lblSW = sW ? ' — ' + sW + '″' : '';
    var lblST = sT ? ' — ' + sT + '″' : '';
    var lblBL = bL ? ' — ' + bL + '″' : '';
    var lblBW = bW ? ' — ' + bW + '″' : '';
    var lblBT = bT ? ' — ' + bT + '″' : '';

    // Seat Length
    var oSL = outward(sA, sD);
    var sla1 = [sFbl[0] + oSL[0] * hug, sFbl[1] + oSL[1] * hug];
    var sla2 = [sFbr[0] + oSL[0] * hug, sFbr[1] + oSL[1] * hug];
    h += '<line x1="' + f(sFbl[0]) + '" y1="' + f(sFbl[1]) + '" x2="' + f(sla1[0]) + '" y2="' + f(sla1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(sFbr[0]) + '" y1="' + f(sFbr[1]) + '" x2="' + f(sla2[0]) + '" y2="' + f(sla2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(sla1[0]) + '" y1="' + f(sla1[1]) + '" x2="' + f(sla2[0]) + '" y2="' + f(sla2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ds-arr)" marker-end="url(#ds-arr)"/>';
    h += '<text x="' + f((sla1[0] + sla2[0]) / 2) + '" y="' + f((sla1[1] + sla2[1]) / 2 + 14) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Length' + lblSL + '</text>';

    // Seat Width
    var oSW = outward(sA, sB);
    var swa1 = [sFbl[0] + oSW[0] * hug, sFbl[1] + oSW[1] * hug];
    var swa2 = [sRbl[0] + oSW[0] * hug, sRbl[1] + oSW[1] * hug];
    h += '<line x1="' + f(sFbl[0]) + '" y1="' + f(sFbl[1]) + '" x2="' + f(swa1[0]) + '" y2="' + f(swa1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(sRbl[0]) + '" y1="' + f(sRbl[1]) + '" x2="' + f(swa2[0]) + '" y2="' + f(swa2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(swa1[0]) + '" y1="' + f(swa1[1]) + '" x2="' + f(swa2[0]) + '" y2="' + f(swa2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#ds-arr)" marker-end="url(#ds-arr)"/>';
    h += '<text x="' + f((swa1[0] + swa2[0]) / 2 - 6) + '" y="' + f((swa1[1] + swa2[1]) / 2 + 4) + '" text-anchor="end" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Width' + lblSW + '</text>';

    // Seat Thickness
    var thx = sFbr[0] + hug;
    h += '<line x1="' + f(sD[0]) + '" y1="' + f(sD[1]) + '" x2="' + f(thx) + '" y2="' + f(sD[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(sFbr[0]) + '" y1="' + f(sFbr[1]) + '" x2="' + f(thx) + '" y2="' + f(sFbr[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thx) + '" y1="' + f(sD[1] + 2) + '" x2="' + f(thx) + '" y2="' + f(sFbr[1] - 2) + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#ds-arr)" marker-end="url(#ds-arr)"/>';
    h += '<text x="' + f(thx + 5) + '" y="' + f((sD[1] + sFbr[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC + '">Thickness' + lblST + '</text>';

    // Back dimension labels
    {
      var blOff = 12;
      var bla1 = [bBLt[0] - blOff * 0.3, bBLt[1] - blOff];
      var bla2 = [bBRt[0] - blOff * 0.3, bBRt[1] - blOff];
      h += '<line x1="' + f(bBLt[0]) + '" y1="' + f(bBLt[1]) + '" x2="' + f(bla1[0]) + '" y2="' + f(bla1[1]) + '" stroke="' + backDimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bBRt[0]) + '" y1="' + f(bBRt[1]) + '" x2="' + f(bla2[0]) + '" y2="' + f(bla2[1]) + '" stroke="' + backDimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bla1[0]) + '" y1="' + f(bla1[1]) + '" x2="' + f(bla2[0]) + '" y2="' + f(bla2[1])
        + '" stroke="' + backDimC + '" stroke-width="1.3" marker-start="url(#ds-arr)" marker-end="url(#ds-arr)"/>';
      h += '<text x="' + f((bla1[0] + bla2[0]) / 2) + '" y="' + f((bla1[1] + bla2[1]) / 2 - 4) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + backDimC + '">Back length' + lblBL + '</text>';

      // Back Width (vertical on left side of back)
      var bwOff = 18;
      var bwa1 = [bBL[0] - bwOff, bBL[1]];
      var bwa2 = [bBLt[0] - bwOff, bBLt[1]];
      h += '<line x1="' + f(bBL[0]) + '" y1="' + f(bBL[1]) + '" x2="' + f(bwa1[0]) + '" y2="' + f(bwa1[1]) + '" stroke="' + backDimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bBLt[0]) + '" y1="' + f(bBLt[1]) + '" x2="' + f(bwa2[0]) + '" y2="' + f(bwa2[1]) + '" stroke="' + backDimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bwa1[0]) + '" y1="' + f(bwa1[1]) + '" x2="' + f(bwa2[0]) + '" y2="' + f(bwa2[1])
        + '" stroke="' + backDimC + '" stroke-width="1.3" marker-start="url(#ds-arr)" marker-end="url(#ds-arr)"/>';
      h += '<text x="' + f((bwa1[0] + bwa2[0]) / 2 - 4) + '" y="' + f((bwa1[1] + bwa2[1]) / 2 + 4) + '" text-anchor="end" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + backDimC + '">Back width' + lblBW + '</text>';

      // Back Thickness (horizontal at top right of back)
      var btOff = 12;
      var bta1 = [bFRt[0] + btOff, bFRt[1]];
      var bta2 = [bBRt[0] + btOff, bBRt[1]];
      h += '<line x1="' + f(bFRt[0]) + '" y1="' + f(bFRt[1]) + '" x2="' + f(bta1[0]) + '" y2="' + f(bta1[1]) + '" stroke="' + backDimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bBRt[0]) + '" y1="' + f(bBRt[1]) + '" x2="' + f(bta2[0]) + '" y2="' + f(bta2[1]) + '" stroke="' + backDimC + '" stroke-width="0.6"/>';
      h += '<line x1="' + f(bta1[0]) + '" y1="' + f(bta1[1]) + '" x2="' + f(bta2[0]) + '" y2="' + f(bta2[1])
        + '" stroke="' + backDimC + '" stroke-width="1.3" marker-start="url(#ds-arr)" marker-end="url(#ds-arr)"/>';
      h += '<text x="' + f((bta1[0] + bta2[0]) / 2 + 5) + '" y="' + f((bta1[1] + bta2[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + backDimC + '">Back T' + lblBT + '</text>';
    }

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    if (state.backStyle === 'firm_back') parts.push('Firm back');
    else if (state.backStyle === 'pillow_back') parts.push('Pillow back');
    if (state.seatL && state.seatW && state.seatT)
      parts.push('Seat ' + state.seatL + '×' + state.seatW + '×' + state.seatT + '″');
    if (state.backL && state.backW && state.backT)
      parts.push('Back ' + state.backL + '×' + state.backW + '×' + state.backT + '″');
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pipLabel = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pipLabel += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pipLabel);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Configure your deep seat cushion below';
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;
    state.seatL = parseFloat(els.seatL.value) || 0;
    state.seatW = parseFloat(els.seatW.value) || 0;
    state.seatT = parseFloat(els.seatT.value) || 0;
    state.backL = parseFloat(els.backL.value) || 0;
    state.backW = parseFloat(els.backW.value) || 0;
    state.backT = parseFloat(els.backT.value) || 0;
    state.extraCovers = els.extraCovers ? els.extraCovers.value : '0';
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) {
        state.pipingColor = 'match';
        if (els.pipingColor) els.pipingColor.value = 'match';
      }
    }

    // Validation highlights
    ['seatL', 'seatW', 'seatT', 'backL', 'backW', 'backT'].forEach(function (id) {
      els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });

    drawCushion();
    updateSummary();

    var seatOk = hasSeatDims();
    var backOk = hasBackDims();
    var ec = parseInt(state.extraCovers) || 0;
    updateExtraCoverLabels();

    var seatPrice = seatOk ? calcPiecePrice(state.seatL, state.seatW, state.seatT) : null;
    var backPrice = backOk ? calcPiecePrice(state.backL, state.backW, state.backT) : null;
    var addonTotal = getAddonTotal();

    if (seatPrice !== null && backPrice !== null) {
      var finalPrice = Math.round(seatPrice + backPrice + addonTotal);
      els.priceMain.textContent = '$' + finalPrice;
      var parts = ['Seat $' + Math.round(seatPrice), 'Back $' + Math.round(backPrice)];
      if (ADDON.piping[state.piping])     parts.push('Piping +$' + ADDON.piping[state.piping]);
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Piping color +$' + ADDON.pipingColor[state.pipingColor]);
      if (ADDON.straps[state.straps])     parts.push('Straps +$' + ADDON.straps[state.straps]);
      if (ADDON.zipper[state.zipper])     parts.push('Zipper +$' + ADDON.zipper[state.zipper]);
      if (ADDON.antislip[state.antislip]) parts.push('Anti-slip +$' + ADDON.antislip[state.antislip]);
      if (ec > 0)                         parts.push('Covers +$' + (extraCoverUnit() * ec));
      if (ADDON.throwPillow[state.throwPillow]) parts.push('Throw Pillow +$' + ADDON.throwPillow[state.throwPillow]);
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('Pillow Covers +$' + ADDON.pillowCovers[state.pillowCovers]);
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armrest +$' + ADDON.armrestPillow[state.armrestPillow]);
      els.priceBreak.textContent = parts.join(' · ');
    } else if (seatPrice !== null) {
      els.priceMain.textContent = '$' + Math.round(seatPrice + addonTotal) + '+';
      els.priceBreak.textContent = 'Seat $' + Math.round(seatPrice) + ' · Enter back dimensions for full price';
    } else {
      els.priceMain.textContent = '$—';
      els.priceBreak.textContent = 'Enter dimensions to see price';
    }

    var complete = isComplete();
    var hasDimPrice = (seatPrice !== null && backPrice !== null);
    var btnPrice = hasDimPrice ? Math.round(seatPrice + backPrice + addonTotal) : (seatPrice !== null ? Math.round(seatPrice + addonTotal) : null);
    var btns = [els.addBtn, els.addBtnSticky].filter(Boolean);
    btns.forEach(function (btn) {
      btn.disabled = !complete;
      if (hasDimPrice) {
        btn.textContent = 'Add to order — $' + btnPrice + ' · Free shipping →';
      } else if (btnPrice !== null) {
        btn.textContent = 'Add to order — $' + btnPrice + '+ →';
      } else {
        btn.textContent = 'Add to order →';
      }
    });

    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Free shipping · Ships in ~20 business days';
      } else {
        var missing = [];
        if (!state.backStyle) missing.push('back style');
        if (!seatOk) missing.push('seat dimensions');
        if (!backOk) missing.push('back dimensions');
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? 'Select ' + missing.join(', ') + ' to continue' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: { price: totalPrice, state: state }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var seatPrice = calcPiecePrice(state.seatL, state.seatW, state.seatT);
    var backPrice = calcPiecePrice(state.backL, state.backW, state.backT);
    var finalPrice = Math.round(seatPrice + backPrice + getAddonTotal());
    var styleLabel = state.backStyle === 'firm_back' ? 'Firm back cushion' : 'Pillow back cushion';
    var props = {
      'Back Style': styleLabel,
      'Seat Length': state.seatL + '"', 'Seat Width': state.seatW + '"', 'Seat Thickness': state.seatT + '"',
      'Back Length': state.backL + '"', 'Back Width': state.backW + '"', 'Back Thickness': state.backT + '"'
    };
    props['Piping'] = LABELS[state.piping] || state.piping;
    props['Piping Color'] = (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Match fabric';
    props['Straps'] = LABELS[state.straps] || state.straps;
    props['Zipper'] = LABELS[state.zipper] || state.zipper;
    props['Anti-slip'] = LABELS[state.antislip] || state.antislip;

    if (state.fabric) {
      props['Fabric'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
      var _grade = extractTag(state.fabric.tags || [], 'grade');
      if (_grade) props['Fabric Grade'] = _grade.charAt(0).toUpperCase() + _grade.slice(1);
    }
    if (ec > 0) props['Extra Covers'] = ec + ' set(s)';
    if (state.throwPillow !== 'none') props['Throw Pillow'] = state.throwPillow.replace('pcs_', ' pcs ');
    if (state.pillowCovers !== 'none') props['Pillow Covers'] = state.pillowCovers.replace('pcs_', ' pcs ');
    if (state.armrestPillow !== 'none') props['Armrest Pillow'] = state.armrestPillow.replace('_', ' ');
    if (state.zipperPlacement) {
      props['Zipper Placement'] = state.zipperPlacement;
    }
    if (state.notes.trim()) props['Customer Note'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    var pieces = [
      { length: state.seatL, width: state.seatW, thickness: state.seatT },
      { length: state.backL, width: state.backW, thickness: state.backT }
    ];

    var apiPayload = {
      pieces: pieces,
      piping: state.piping, pipingColor: state.pipingColor, straps: state.straps,
      zipper: state.zipper, antislip: state.antislip,
      extraCovers: ec, throwPillow: state.throwPillow,
      pillowCovers: state.pillowCovers, armrestPillow: state.armrestPillow
    };

    return { properties: props, price: finalPrice, apiPayload: apiPayload };
  }

  /* ─── DOM transformation ─── */
  function transformDOM() {
    // Show back dimension inputs
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) {
      var el = backDims[i];
      el.style.display = el.classList.contains('cc-sp-dims') ? 'grid' : 'block';
    }

    // Show "Seat" label
    var seatLabel = document.querySelector('.cc-seat-label');
    if (seatLabel) seatLabel.style.display = '';

    // Adjust SVG viewBox for seat + back
    var svg = document.getElementById('cc-dim-svg');
    if (svg) {
      svg.setAttribute('viewBox', '0 0 480 290');
      svg.style.maxHeight = '220px';
    }

    // Replace Shape options with back style: Firm back vs Pillow back
    var shapeGrid = document.getElementById('cc-shape-grid');
    if (shapeGrid) {
      shapeGrid.classList.remove('g4');
      shapeGrid.classList.add('g2');
      shapeGrid.innerHTML = ''
        + '<div class="cc-opt-card" data-group="backStyle" data-value="firm_back">'
        +   '<div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/deep_seat_cushion.webp?v=1783587272" alt="Firm back cushion"></div>'
        +   '<div class="cc-opt-name">Firm back</div><div class="cc-opt-price">included</div>'
        + '</div>'
        + '<div class="cc-opt-card" data-group="backStyle" data-value="pillow_back">'
        +   '<div class="cc-opt-img"><img src="https://cdn.shopify.com/s/files/1/0676/5687/6232/files/Seat_and_Back_Cushion_Set.webp?v=1783587272" alt="Pillow back cushion"></div>'
        +   '<div class="cc-opt-name">Pillow back</div><div class="cc-opt-price">included</div>'
        + '</div>';
    }

    // Hide price area above SVG
    var spTop = document.querySelector('.cc-sp-top');
    if (spTop) spTop.style.display = 'none';
    var priceBreakDiv = document.getElementById('cc-price-break');
    if (priceBreakDiv) priceBreakDiv.style.display = 'none';

    // Tighten label spacing
    var dimsLabels = document.querySelectorAll('.cc-dims-label');
    for (var j = 0; j < dimsLabels.length; j++) {
      dimsLabels[j].style.marginBottom = '0';
      dimsLabels[j].style.paddingBottom = '0';
    }

    // Move dimension inputs out of sticky container
    var stickyWrap = document.getElementById('cc-sticky-preview');
    if (stickyWrap) {
      var dimsContainer = document.createElement('div');
      dimsContainer.className = 'cc-dims-outside';

      var seatLbl = stickyWrap.querySelector('.cc-seat-label');
      var seatDims = stickyWrap.querySelector('.cc-sp-dims:not(.cc-back-dims)');
      var backLbl = stickyWrap.querySelector('.cc-back-label');
      var backDimsEl = stickyWrap.querySelector('.cc-sp-dims.cc-back-dims');
      var hint = stickyWrap.querySelector('.cc-sp-hint');

      if (seatLbl) dimsContainer.appendChild(seatLbl);
      if (seatDims) dimsContainer.appendChild(seatDims);
      if (backLbl) dimsContainer.appendChild(backLbl);
      if (backDimsEl) dimsContainer.appendChild(backDimsEl);
      if (hint) dimsContainer.appendChild(hint);

      stickyWrap.parentNode.insertBefore(dimsContainer, stickyWrap.nextSibling);
    }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.seatL        = document.getElementById('cc-dim-l');
    els.seatW        = document.getElementById('cc-dim-w');
    els.seatT        = document.getElementById('cc-dim-t');
    els.backL        = document.getElementById('cc-back-l');
    els.backW        = document.getElementById('cc-back-w');
    els.backT        = document.getElementById('cc-back-t');
    els.extraCovers    = document.getElementById('cc-extra-covers');
    els.throwPillow    = document.getElementById('cc-throw-pillow');
    els.pillowCovers   = document.getElementById('cc-pillow-covers');
    els.armrestPillow  = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    ['seatL', 'seatW', 'seatT', 'backL', 'backW', 'backT'].forEach(function (id) {
      if (els[id]) els[id].addEventListener('input', recalc);
    });
    if (els.extraCovers) els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) {
      state.fabric = e.detail;
      recalc();
    });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) {
      state.fabric = existingFabric;
      recalc();
    }
    setInterval(function () {
      var f = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (f && f !== state.fabric) {
        state.fabric = f;
        recalc();
      }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.DeepSeatCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
